import last from 'lodash/last';
import type { LevelTasksTracker, LevelTaskItem } from './LevelTasksTracker';
import type { BoardModel } from '../boardMVC/models/board/BoardModel';
import type { BlockerModel } from '../boardMVC/types';
import { MeasureTapeBlockerModel } from '../boardMVC/models/blockers/MeasureTapeBlockerModel';
import { BoardEventType, TILE_MATCH_REASONS } from '../boardMVC/models/board/BoardEvents';
import { LevelTaskType } from './types';
import type { TrayModel } from '../boardMVC/models/tray/TrayModel';
import type { BoardView } from '../boardMVC/views/board/BoardView';
import type { TileModel } from '../boardMVC/models/board/TileModel';

interface Config {
  levelTaskTracker: LevelTasksTracker;
  boardModel: BoardModel;
  trayModel?: TrayModel;
  boardView: BoardView | null;
  animateBoardProgress?: (tilesOrBlocker: BlockerModel | TileModel[]) => void;
}

export function connectTaskTrackerToBoard(config: Config): void {
  const { levelTaskTracker, boardModel, trayModel, boardView, animateBoardProgress } = config;

  const updateBlockerTaskProgress = (taskType: LevelTaskType): void => {
    const taskProgress = levelTaskTracker.getTaskProgress(taskType);
    if (taskProgress) {
      boardModel.onLevelBlockerTaskUpdate(taskType, taskProgress);
    }
  };

  // trayModel.onRefilled.connect(this.saveLevelData.bind(this));

  const trackBlockerTask = (blocker: BlockerModel): void => {
    if (blocker instanceof MeasureTapeBlockerModel && !blocker.isCase) return;

    const taskType = levelTaskTracker.getItemTaskType(blocker);

    if (!taskType || !levelTaskTracker.isActiveTask(taskType) || (boardView && !boardView.getBlockerView(blocker.id)))
      return;
    trayModel?.updateProgress(levelTaskTracker.getCombinedProgress());
    levelTaskTracker.trackTaskItem(blocker as LevelTaskItem);
    animateBoardProgress?.(blocker);

    updateBlockerTaskProgress(taskType);

    if (!levelTaskTracker.isActiveTask(taskType)) {
      boardModel.onLevelTaskComplete(taskType);
    }
  };

  boardModel.events.addListener(BoardEventType.BlockerRemoved, ({ blocker }) => {
    trackBlockerTask(blocker);
  });

  boardModel.events.addListener(BoardEventType.CollectableRelease, ({ stack }) => {
    if (!stack.blocker) return;
    trackBlockerTask(stack.blocker);
  });

  boardModel.events.addListener(BoardEventType.TilesRemoved, ({ reason, tiles }) => {
    if (!TILE_MATCH_REASONS.includes(reason) || !levelTaskTracker.isActiveTask(LevelTaskType.Hexes)) return;

    const lastTile = last(tiles);
    if (!lastTile) return;

    if (boardView) {
      const tileView = boardView.getTileView(lastTile.id);
      if (!tileView) return;
    }

    tiles.forEach((tile) => {
      const updatedTask = levelTaskTracker.trackTaskItem(tile);
      if (updatedTask) {
        trayModel?.updateProgress(levelTaskTracker.getCombinedProgress());
      }
    });

    animateBoardProgress?.(tiles);
  });

  levelTaskTracker.tasks.forEach((task) => {
    updateBlockerTaskProgress(task.type);
  });

  levelTaskTracker.getCompletedTaskTypes().forEach((taskType) => {
    boardModel.onLevelTaskComplete(taskType);
  });
}
